/**
 * OwnerDashboardRightRail — Desktop-only (lg:) right column on the Owner
 * Overview. Four stacked cards:
 *   1. Recent Activity feed (orders, reviews, verification / plan changes)
 *   2. 30-day Orders Summary
 *   3. Refer a Business (earn TBC Points)
 *   4. "Together We Build More" editorial banner → /founding-members
 *
 * All data is fetched here so the parent Overview stays clean. Every
 * fetch is optional — if one endpoint fails the rest of the rail still
 * renders. Cards are compact + link into the existing owner tabs.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShoppingBag, Star, BadgeCheck, Crown, Share2, Copy, Sparkles,
  ArrowUpRight, Users, DollarSign, Clock, ChevronRight,
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(iso).toLocaleDateString();
}

function ActivityIcon({ type }) {
  const cls = "w-3.5 h-3.5";
  if (type === "order") return <ShoppingBag className={cls} />;
  if (type === "review") return <Star className={cls} />;
  if (type === "verification") return <BadgeCheck className={cls} />;
  if (type === "plan") return <Crown className={cls} />;
  return <Sparkles className={cls} />;
}

export default function OwnerDashboardRightRail({ business, onSelectTab }) {
  const businessId = business?.business_id;
  const [activity, setActivity] = useState([]);
  const [ordersSummary, setOrdersSummary] = useState(null);
  const [referral, setReferral] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(true);

  useEffect(() => {
    if (!businessId) return;
    let cancel = false;
    setLoadingActivity(true);
    Promise.all([
      axios.get(`${API}/analytics/business/${businessId}/recent-activity?limit=6`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/analytics/business/${businessId}/orders-summary?days=30`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/referrals/me`, { withCredentials: true }).catch(() => null),
    ]).then(([act, ord, ref]) => {
      if (cancel) return;
      setActivity(act?.data?.activity || []);
      setOrdersSummary(ord?.data || null);
      setReferral(ref?.data || null);
      setLoadingActivity(false);
    });
    return () => { cancel = true; };
  }, [businessId]);

  const copyReferralLink = async () => {
    if (!referral?.share_url) return;
    try {
      await navigator.clipboard.writeText(referral.share_url);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <aside
      className="hidden lg:flex flex-col gap-4 w-[320px] flex-none"
      data-testid="owner-right-rail"
    >
      {/* Recent Activity */}
      <div className="rounded-2xl border border-tbc-navy/10 bg-white p-4" data-testid="owner-recent-activity">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-navy/60 font-semibold">Recent Activity</p>
        </div>
        {loadingActivity ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-10 rounded-md bg-tbc-cream/40 animate-pulse" />
            ))}
          </div>
        ) : activity.length === 0 ? (
          <p className="text-xs text-tbc-navy/50 py-3">
            No activity yet. Your first order, review, or verification will land here.
          </p>
        ) : (
          <ul className="space-y-2.5">
            {activity.map((row) => (
              <li key={`${row.type}:${row.id}`}>
                <button
                  type="button"
                  onClick={() => onSelectTab?.(row.link || "overview")}
                  className="w-full flex items-start gap-2.5 text-left hover:bg-tbc-cream/40 rounded-md px-2 py-1.5 transition-colors"
                  data-testid={`owner-activity-${row.type}`}
                >
                  <div className="mt-0.5 w-6 h-6 rounded-full bg-tbc-gold/15 text-tbc-gold flex items-center justify-center flex-none">
                    <ActivityIcon type={row.type} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-tbc-navy leading-snug truncate">{row.title}</p>
                    {row.sub && (
                      <p className="text-[11px] text-tbc-navy/55 leading-snug truncate">{row.sub}</p>
                    )}
                    <p className="text-[10px] text-tbc-navy/40 mt-0.5">{timeAgo(row.created_at)}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Orders Summary */}
      <div
        className="rounded-2xl border border-tbc-navy/10 bg-white p-4"
        data-testid="owner-orders-summary"
      >
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-navy/60 font-semibold">Orders · 30d</p>
          <button
            type="button"
            onClick={() => onSelectTab?.("orders")}
            className="text-[10px] font-semibold text-tbc-gold hover:text-tbc-gold/80 flex items-center gap-0.5"
            data-testid="owner-orders-summary-view"
          >
            View <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <SummaryStat
            icon={ShoppingBag}
            value={ordersSummary?.total_orders ?? 0}
            label="Orders"
          />
          <SummaryStat
            icon={DollarSign}
            value={`$${(ordersSummary?.revenue_usd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            label="Revenue"
          />
          <SummaryStat
            icon={Clock}
            value={ordersSummary?.pending_fulfillment ?? 0}
            label="Pending"
          />
        </div>
      </div>

      {/* Refer a Business */}
      <div className="rounded-2xl bg-tbc-cream border border-tbc-gold/40 p-4" data-testid="owner-refer-panel">
        <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-gold font-semibold flex items-center gap-1">
          <Share2 className="w-3 h-3" /> Refer a Business
        </p>
        <h3 className="font-heading text-lg text-tbc-navy mt-1 leading-tight">Earn TBC Points</h3>
        <p className="text-xs text-tbc-navy/65 mt-1 leading-snug">
          Invite a Black-owned business. Earn 50 pts on signup + 100 pts when they upgrade.
        </p>
        {referral?.share_url ? (
          <>
            <div className="mt-3 rounded-md bg-white border border-tbc-navy/10 px-2 py-1.5 text-[11px] font-mono text-tbc-navy truncate">
              {referral.share_url}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                onClick={copyReferralLink}
                className="bg-tbc-navy text-white hover:bg-tbc-navy/90 text-xs h-8 flex-1"
                data-testid="owner-refer-copy"
              >
                <Copy className="w-3 h-3 mr-1" /> Copy invite link
              </Button>
            </div>
            <div className="flex items-center gap-3 mt-3 text-[11px] text-tbc-navy/70">
              <span className="inline-flex items-center gap-1">
                <Users className="w-3 h-3" />
                {referral.total_referrals || 0} referred
              </span>
              <span className="text-tbc-navy/30">·</span>
              <span className="inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-tbc-gold" />
                {(referral.signup_points_earned || 0) + (referral.activation_points_earned || 0)} pts
              </span>
            </div>
          </>
        ) : (
          <p className="mt-3 text-[11px] text-tbc-navy/50">Loading your invite link…</p>
        )}
      </div>

      {/* Together We Build More banner */}
      <Link
        to="/founding-members"
        className="rounded-2xl bg-gradient-to-br from-tbc-navy to-tbc-navy/85 text-white p-4 block hover:shadow-lg transition-shadow"
        data-testid="owner-editorial-banner"
      >
        <p className="text-[10px] uppercase tracking-[0.28em] text-tbc-gold font-semibold flex items-center gap-1">
          <Crown className="w-3 h-3" /> Together We Build More
        </p>
        <h3 className="font-heading text-lg mt-1 leading-tight">Meet the Founding 100</h3>
        <p className="text-[11px] text-white/70 mt-1 leading-snug">
          The businesses whose belief built this movement. See the roll of honour.
        </p>
        <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-tbc-gold">
          Visit the wall <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
      </Link>
    </aside>
  );
}

function SummaryStat({ icon: Icon, value, label }) {
  return (
    <div className="rounded-lg bg-tbc-cream/50 p-2 text-center">
      <Icon className="w-3.5 h-3.5 text-tbc-navy/60 mx-auto" />
      <p className="font-heading text-base text-tbc-navy leading-none mt-1">
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
      <p className="text-[9px] uppercase tracking-wider text-tbc-navy/50 mt-0.5">{label}</p>
    </div>
  );
}
